import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import RemarkDialog from "@components/dialog/remarkDialog";

import { useSnackbar } from "@context/snackbarContext";
import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { ACTION } from "@utils/constant";

import { useAuthentication } from "@redux/authentication/hook";

import QuestionListing from "./questionListing";
import AddEditQuestionDialog from "./addEditQuestionDialog";
import ViewQuestionDialog from "./viewQuestionDialog";
import { formatDate, formatDateTime } from "@utils/formatter";
import CloneFormDialog from "./cloneFormDialog";

const schema = z.object({
  formName: z.string().min(1, 'Form Name is required'),
  description: z.string().optional(),

  /* view only */
  postName: z.string().optional(),
  postDate: z.string().optional(),
  status: z.string().optional(),
  voidBy: z.string().optional(),
  voidDate: z.string().optional(),
  voidRemark: z.string().optional(),
});

export default function FormForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, watch } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      
    },
  });

  const [openAddEditQuestionDialog, setOpenAddEditQuestionDialog] = useState(false);
  const [addEditQuestionDialogType, setAddEditQuestionDialogType] = useState(false);
  const [openViewQuestionDialog, setOpenViewQuestionDialog] = useState(false)
  
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  
  const [openCloneDialog, setOpenCloneDialog] = useState(false)
  const [openVoidDialog, setOpenVoidDialog] = useState(false)
  
  const [questionListing, setQuestionListing] = useState([])
  const [selectedQuestion, setSelectedQuestion] = useState(null)

  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchFormMutation.mutate(id)
    }
  },[pageAction])

  const fetchFormMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getForm}/${id}`)
    },
    onSuccess: (res) => {
      // console.log(res.data)
      const data = res.data
      data.status = data.status ? "Valid" : "Voided"
      data.voidDate = data.voidDate ? formatDateTime(data.voidDate) : '-'
      data.voidBy = data.voidBy ? data.voidBy : '-'
      data.voidRemark = data.voidRemark ? data.voidRemark : '-'
      data.postName = data?.posts.length > 0 ? data?.posts[0].postName : '-'
      data.postDate = data?.posts.length > 0 ? formatDate(data?.posts[0].postDate) : '-'
      reset(data)
      data.formQuestions = data.formQuestions.map(item => ({
        ...item,
        ...item.question,
        createdOn: formatDateTime(item.createdOn)
      }));
      const sortedFormQuestion = data.formQuestions?.sort((a, b) => a.sequence - b.sequence)
      setQuestionListing(sortedFormQuestion)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve form", "error");
    }
  });

  const createFormMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createForm, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getForm'] })
      openSnackbar("Successfully created form", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create form", "error");
    }
  })

  const updateFormMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateForm}/${id}`, {...data, by: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getForm'] })
      openSnackbar("Successfully updated form", "success");
      fetchFormMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update form", "error");
    }
  })

  const cloneFormMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${AdminApiEndpoints.cloneForm}/${id}`, {...data, by: user?.username})
    },
    onSuccess: (res) => {
      // console.log(res)
      setOpenCloneDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getForm'] })
      openSnackbar("Successfully cloned form", "success");
      fetchFormMutation.mutate(id)
    },
    onError: (err) => {
      setOpenCloneDialog(false)
      if (err?.response) openSnackbar(err?.response?.data?.message, "error");
      else openSnackbar("Failed to clone form", "error");
    }
  })

  const voidFormMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.voidForm}/${id}`, {voidRemark: data?.remark, by: user?.username})
    },
    onSuccess: () => {
      setOpenVoidDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getForm'] })
      openSnackbar("Successfully voided form", "success");
      fetchFormMutation.mutate(id)
    },
    onError: (err) => {
      setOpenVoidDialog(false)
      if (err?.response) openSnackbar(err?.response?.data?.message, "error");
      else openSnackbar("Failed to void form", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    data.formQuestion = questionListing.map(item => ({
      questionGuid: item?.questionGuid,
      sequence: item?.sequence,
      data: item?.data ? item?.data : null
    }));
    // console.log(data)
    // return
    if(pageAction === "create")
      createFormMutation.mutate(data)
    else if(pageAction === "edit")
      updateFormMutation.mutate(data)
  }

  const onSubmitVoid = (data) => {
    voidFormMutation.mutate(data)
  }

  const onSubmitClone = (data) => {
    cloneFormMutation.mutate(data)
  }

  const onBack = () => {
    navigate('/admin/form-setup', { replace: true });
  }

  const watchStatus = watch('status')

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (["edit"].includes(pageAction) && !fetchFormMutation.isPending && !fetchFormMutation.isError) &&
            <div style={{display: 'flex', gap: "10px"}}>
              <CustomButton color="#A1C558" disabled={watchStatus === "Voided"} onClick={()=>setOpenCloneDialog(true)}>
                Clone
              </CustomButton>
              <CustomButton color="#D13F58" disabled={watchStatus === "Voided"} onClick={()=>setOpenVoidDialog(true)}>
                Void
              </CustomButton>
            </div>
        }
      />
      <SkeletonWrapper isLoading={fetchFormMutation.isPending} isError={fetchFormMutation.isError} errorMessage="Unable to load form" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Form Detail</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="formName"
                control={control}
                label="Form Name"
                disabled={fetchFormMutation.isPending || fetchFormMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="description"
                control={control}
                label="Description"
                disabled={fetchFormMutation.isPending || fetchFormMutation.isError}
                readOnly={["view"].includes(pageAction)}
                multiline
                rows={2}
              />
            </Grid2>

            {["view", "edit"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, xs: 12 }}>
              <ControlledTextField 
                name="postName"
                control={control}
                label="Post Name"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, xs: 12 }}>
              <ControlledTextField 
                name="postDate"
                control={control}
                label="Post Date"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, xs: 12 }}>
              <ControlledTextField 
                name="status"
                control={control}
                label="Status"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, xs: 12 }}>
              <ControlledTextField 
                name="voidBy"
                control={control}
                label="Void By"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, xs: 12 }}>
              <ControlledTextField 
                name="voidDate"
                control={control}
                label="Void Date"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, xs: 12 }}>
              <ControlledTextField 
                name="voidRemark"
                control={control}
                label="Void Remark"
                disabled={true}
              />
            </Grid2>

            </>}

          </Grid2>
        </Paper>

        <QuestionListing 
          pageAction={pageAction}
          setOpenAddEditQuestionDialog={setOpenAddEditQuestionDialog}
          setAddEditQuestionDialogType={setAddEditQuestionDialogType}
          setOpenViewQuestionDialog={setOpenViewQuestionDialog}
          selectedQuestion={selectedQuestion}
          setSelectedQuestion={setSelectedQuestion}
          questionListing={questionListing}
          setQuestionListing={setQuestionListing}
          disabled={watchStatus === "Voided"}
        />
        
        {(["create"].includes(pageAction) || (["edit"].includes(pageAction) && watchStatus === "Valid")) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              overflowX: "auto",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={createFormMutation.isPending || fetchFormMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <AddEditQuestionDialog 
        open={openAddEditQuestionDialog}
        onClose={()=>setOpenAddEditQuestionDialog(false)}
        type={addEditQuestionDialogType}

        questionListing={questionListing}
        setQuestionListing={setQuestionListing}
        selectedQuestion={selectedQuestion}
      />
      <ViewQuestionDialog 
        open={openViewQuestionDialog}
        onClose={()=>setOpenViewQuestionDialog(false)}
        selectedQuestion={selectedQuestion}
      />
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription={`Submit form?`}
        onSubmit={()=>onConfirmSubmit()}
        loading={createFormMutation.isPending || updateFormMutation.isPending}
      />
      <RemarkDialog 
        open={openVoidDialog}
        onClose={()=>setOpenVoidDialog(false)}
        dialogTitle="Void Form"
        onSubmit={onSubmitVoid}
        loading={voidFormMutation.isPending}
      />
      <CloneFormDialog 
        open={openCloneDialog}
        onClose={()=>setOpenCloneDialog(false)}
        onSubmit={onSubmitClone}
        loading={cloneFormMutation.isPending}
      />
    </div>
  );
}
